import { PackageSuggester } from '../../../../components/PackageSuggester';
import { PackageTypeSelector } from './PackageTypeSelector';
import { Inputty } from '../../../../Inputty';
import {
  Box,
  Button,
  FormField,
  Grid,
  Link,
  Popover,
} from '@amzn/awsui-components-console';
import styles from './PackageForm.module.sass';
import { ProtoPBI } from './ProtoPBI';
import { ListUpdater } from './ListUpdater';
import { BRANCH_NAME_PATTERN, COMMIT_ID_PATTERN } from './packageValidation';
import { isAutoMergeEligible } from './autoMergeEligibility';
import commitStyles from '../commitId.module.sass';
import { useState } from 'react';
import { useAuthState } from '../../../../authentication/context';
import { callApi } from '../../../../client';
import { GetTipOfBranchCommand } from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import { NoTokenError } from '../../../../errors/NoTokenError';
import { useApiErrors } from '../../../../components/ApiErrorContext';

export type PackageFormProps = {
  item: ProtoPBI;
  editing: boolean;
  update: ReturnType<ListUpdater>;
  remove: () => void;
  validate: boolean;
  onCommitIdFetched?: (commitId: string) => void;
};
export const PackageForm = ({
  editing,
  item,
  update,
  validate,
  remove,
  onCommitIdFetched,
}: PackageFormProps) => {
  const { token } = useAuthState();
  const [loading, setLoading] = useState(false);
  const { pushError } = useApiErrors();

  // A Destination branch (post-conference auto-merge) is only meaningful for auto-merge-eligible
  // package types. For any other type the field is cleared and disabled, and the API rejects a
  // destination branch outright — see autoMergeEligibility + the backend validateDestinationBranch.
  const autoMergeEligible = isAutoMergeEligible(item.packageType);

  const handlePullTipOfBranch = async () => {
    if (!token) throw new NoTokenError();
    if (!item.packageName || !item.branch) {
      return;
    }

    setLoading(true);
    try {
      const command = new GetTipOfBranchCommand({
        packageName: item.packageName,
        branchName: item.branch,
      });

      const response = await callApi(command, token);

      if (response.$metadata.httpStatusCode === 200 && response.commitId) {
        onCommitIdFetched?.(response.commitId);
      }
    } catch (err: any) {
      console.error('Error fetching tip of branch:', err);

      const statusCode = err.$metadata?.httpStatusCode || err.statusCode || 0;
      let message: string;

      if (statusCode === 400) {
        message = `Package "${item.packageName}" or branch "${item.branch}" not found. Please verify the package and branch exist.`;
      } else if (statusCode === 403) {
        message = `FLC doesn't have permission to access package "${item.packageName}". See: https://alpha.www.docs.aws.a2z.com/fusion/latest/userguide/flc-using-flc.html#flc-build-permissions.`;
      } else if (statusCode === 404) {
        message = `Invalid package name or branch name. Please check your input.`;
      } else if (statusCode >= 500) {
        message = `Internal server error occurred. Please try again later.`;
      } else {
        message = `Failed to fetch commit ID: ${
          err.message || 'Unknown error occurred'
        }`;
      }
      pushError('Failed to fetch commit ID', message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Grid
        className={styles.package}
        gridDefinition={
          editing
            ? [
                { colspan: 2 },
                { colspan: 2 },
                { colspan: 2 },
                { colspan: 3 },
                { colspan: 2 },
                { colspan: 1 },
              ]
            : [
                { colspan: 2 },
                { colspan: 2 },
                { colspan: 2 },
                { colspan: 2 },
                { colspan: 4 },
              ]
        }
      >
        <div>
          <FormField description='Name of the package' label='Package name'>
            <PackageSuggester
              disabled={!editing}
              ariaRequired={true}
              packageName={item.packageName}
              onChange={update('packageName')}
            />
          </FormField>
        </div>
        <FormField
          description='Select package type'
          label='Package type'
          info={
            <Popover
              content={
                <>
                  The package type tells FLC how to build each package and which
                  dependencies it needs, so choosing the right one matters. See{' '}
                  <Link
                    external
                    href='https://alpha.www.docs.aws.a2z.com/fusion/latest/userguide/flc-using-flc.html#flc-package-types'
                  >
                    Supported package types
                  </Link>{' '}
                  in the <i>Fusion User Guide</i> for guidance on choosing the
                  correct type.
                </>
              }
              position='top'
              size='large'
              triggerType='custom'
            >
              <span style={{ cursor: 'pointer', color: '#0972d3' }}>Info</span>
            </Popover>
          }
        >
          <PackageTypeSelector
            editing={editing}
            item={item}
            onChange={update('packageType')}
          />
        </FormField>
        <div>
          <FormField description='Select branch for the package' label='Branch'>
            <Inputty
              disabled={!editing}
              ariaRequired={true}
              value={item.branch ? item.branch : ''}
              onChange={update('branch')}
              regex={BRANCH_NAME_PATTERN}
            />
          </FormField>
        </div>
        <div className={commitStyles.commitId}>
          <FormField
            description='The full 40-character commit hash'
            label='Commit ID'
          >
            <div className={commitStyles.commitIdInputWrapper}>
              <Inputty
                disabled={!editing}
                ariaRequired={true}
                value={item.commitId}
                onChange={update('commitId')}
                regex={validate && COMMIT_ID_PATTERN}
              />
              {editing && (
                <div
                  className={commitStyles.tipButton}
                  title='Automatically fetch the latest commit ID from the tip of the selected branch'
                >
                  <Button
                    variant='icon'
                    iconName='arrow-down'
                    onClick={handlePullTipOfBranch}
                    disabled={!item.packageName || !item.branch || loading}
                    loading={loading}
                    ariaLabel='Pull tip of branch (auto-populate commit ID)'
                  />
                </div>
              )}
            </div>
          </FormField>
        </div>
        <div>
          <FormField
            description={
              autoMergeEligible
                ? 'Optional. Merge target (e.g., mainline)'
                : 'Only available for package types FLC can auto-merge after a conference'
            }
            label={
              <span>
                Post-launch destination branch <i>- optional</i>
              </span>
            }
            info={
              <Popover
                header='Post-launch destination branch'
                content={
                  <>
                    Optionally specify a branch (such as <b>mainline</b>) that
                    Fusion attempts to merge your conference content into at the{' '}
                    <b>Resolve venue time</b> (after venue content is
                    published). If you receive an email from Fusion that it
                    can&apos;t merge the content, or you don&apos;t specify a
                    destination branch, you must manually merge your content
                    after your venue content is published. See{' '}
                    <Link
                      external
                      href='https://alpha.www.docs.aws.a2z.com/fusion/latest/userguide/flc-post-conference.html'
                    >
                      Post-conference cleanup
                    </Link>{' '}
                    in the <i>Fusion User Guide</i>.
                  </>
                }
                position='top'
                size='medium'
                triggerType='custom'
              >
                <span style={{ cursor: 'pointer', color: '#0972d3' }}>
                  Info
                </span>
              </Popover>
            }
          >
            <Inputty
              disabled={!editing || !autoMergeEligible}
              ariaRequired={false}
              value={item.publishingBranch ?? ''}
              onChange={update('publishingBranch')}
              regex={item.publishingBranch ? BRANCH_NAME_PATTERN : false}
            />
          </FormField>
        </div>
        {editing && (
          <Button
            disabled={!editing}
            iconName={'close'}
            variant='icon'
            ariaLabel={'Remove'}
            onClick={remove}
          />
        )}
      </Grid>
    </Box>
  );
};
